if (!customElements.get('image-overlay-slider')) {
  customElements.define('image-overlay-slider', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-index]')];
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      this.inView = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      const controls = this.querySelector('.ios-slider__controls');
      if (controls) controls.hidden = false;
      listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-previous')) this.show(this.index - 1);
        if (button.hasAttribute('data-next')) this.show(this.index + 1);
        if (button.hasAttribute('data-index')) this.show(Number(button.dataset.index));
        if (button.hasAttribute('data-pause')) {
          this.paused = !this.paused;
          button.setAttribute('aria-pressed', String(this.paused));
          button.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
          button.textContent = this.paused ? '▶' : 'Ⅱ';
          this.schedule();
        }
      });
      listen(this, 'keydown', event => {
        if (!event.target.closest('.ios-slider__controls')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
      listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => this.schedule());
      listen(this, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.show(index);
      });
      listen(this, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      listen(this, 'touchstart', event => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
        clearTimeout(this.timer);
      });
      listen(this, 'touchend', event => {
        if (this.touch) {
          const dx = event.changedTouches[0].clientX - this.touch.x;
          const dy = event.changedTouches[0].clientY - this.touch.y;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
        }
        this.touch = null;
        this.schedule();
      });
      listen(this, 'touchcancel', () => { this.touch = null; this.schedule(); });
      this.resize = new ResizeObserver(entries => {
        entries.forEach(({ target }) => {
          target.closest('[data-slide]').style.setProperty('--ios-content-height', `${Math.ceil(target.getBoundingClientRect().height) + 100}px`);
        });
      });
      this.querySelectorAll('.ios-slider__content').forEach(content => this.resize.observe(content));
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      });
      this.observer.observe(this);
      this.show(0);
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      const playing = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.paused && !this.hovered && !this.focused && !this.editorSelected && !document.hidden && this.inView && !this.motion.matches;
      this.querySelector('.ios-slider__slides').setAttribute('aria-live', playing ? 'off' : 'polite');
      if (playing) this.timer = setTimeout(() => this.show(this.index + 1), Number(this.dataset.interval) || 5000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
    }
  });
}
